import { useEffect, useState } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getProfile, AccountDeactivatedError } from '@/services/UserManagmentServices';

type RedirectStatus = 'checking' | 'redirect' | 'landing';

export function useAuthenticatedRootRedirect(): RedirectStatus {
  const [status, setStatus] = useState<RedirectStatus>('checking');
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  useEffect(() => {
    // In-app PUSH navigation (e.g. clicking logo) — skip check, show landing immediately
    if (navigationType !== 'POP') {
      setStatus('landing');
      return;
    }

    const controller = new AbortController();
    // 5s timeout: if getProfile hangs (backend unreachable), fall back to landing
    const timeout = setTimeout(() => controller.abort(), 5000);

    (async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (!token) {
          setStatus('landing');
          return;
        }

        const profile = await getProfile(token);

        if (profile?.defaultRole === 'TEACHER') {
          navigate('/teacher-dashboard', { replace: true });
          setStatus('redirect');
        } else if (profile?.defaultRole === 'STUDENT') {
          const dest = profile.goalOnboardingCompleted
            ? '/student-dashboard'
            : '/goal-onboarding';
          navigate(dest, { replace: true });
          setStatus('redirect');
        } else {
          // No defaultRole or no roles — fall back to landing.
          // Deliberately does NOT route to /select-role (unlike OAuthCallback) to avoid
          // a refresh loop if the user reloads /select-role with no role set yet.
          setStatus('landing');
        }
      } catch (err) {
        if (err instanceof AccountDeactivatedError) {
          navigate('/account-deactivated', { replace: true });
          setStatus('redirect');
          return;
        }
        // Covers: fetchAuthSession throws, getProfile throws, AbortController abort (5s)
        setStatus('landing');
      } finally {
        clearTimeout(timeout);
      }
    })();

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return status;
}
